// MembershipUtils.ts

import {emitAccessUpdate} from "../components/Navbar";
import { hasActiveSubscription } from "./SubscriptionUtils";
import { consumeCredit, getUserCredits } from "./CreditUtils";
import { auth } from "../firebaseConfig";

const SEARCHED_QUERIES_KEY = "searched_queries";

export class MembershipUtils {
    // 增加一次访问记录（返回是否允许访问）- uses backend credit system
    static async recordAccess(query: string): Promise<boolean> {
        MembershipUtils.reportSearchToGTM(query).then(r => console.log('report done'));
        
        if (!query) {
            return true; // No query, allow access
        }

        const user = auth.currentUser;
        // Check subscription first
        const hasSub = await hasActiveSubscription();
        if (hasSub) {
            const normalizedQ = query.toLowerCase().trim();
            MembershipUtils.saveSearchedQuery(normalizedQ);
            return true;
        }

        if (!user) {
            console.log('No user logged in');
            return false;
        }

        // Check if query already exists in localStorage (skip credit consumption)
        const normalizedQuery = query.toLowerCase().trim();
        if (MembershipUtils.isQuerySearched(normalizedQuery)) {
            console.log('Query already searched, skipping credit consumption:', normalizedQuery);
            return true;
        }

        try {
            // Consume 1 credit via backend API
            const result = await consumeCredit(user.uid, query);
            emitAccessUpdate();
            
            if (result.success) {
                // Save query to localStorage after successful credit consumption
                MembershipUtils.saveSearchedQuery(normalizedQuery);
                console.log('Credit consumed, remaining balance:', result.balance);
                // Emit event for components to react to credit changes
                window.dispatchEvent(new CustomEvent('credits-updated'));
                return true;
            } else {
                console.log('Failed to consume credit');
                return false;
            }
        } catch (error: any) {
            console.error('Error consuming credit:', error);
            // Check if it's an insufficient credits error
            if (error.code === 'functions/resource-exhausted' || 
                error.message?.includes('Insufficient credits')) {
                return false;
            }
            // For other errors, deny access to be safe
            return false;
        }
    }

    // Check if query has been searched before
    private static isQuerySearched(query: string): boolean {
        const searches = MembershipUtils.getSearchedQueries();
        return searches.includes(query);
    }

    // Save query to localStorage
    private static saveSearchedQuery(query: string): void {
        const searches = MembershipUtils.getSearchedQueries();
        if (!searches.includes(query)) {
            searches.push(query);
            localStorage.setItem(SEARCHED_QUERIES_KEY, JSON.stringify(searches));
        }
    }

    // Get all searched queries from localStorage
    private static getSearchedQueries(): string[] {
        const data = localStorage.getItem(SEARCHED_QUERIES_KEY);
        if (!data) return [];
        try {
            return JSON.parse(data);
        } catch {
            return [];
        }
    }

    // Check if user has credits remaining
    static async isLimit(): Promise<boolean> {
        const user = auth.currentUser;
        if (!user) {
            return true; // No user = limited
        }

        // Pro subscribers are never limited
        const hasSub = await hasActiveSubscription();
        if (hasSub) {
            return false;
        }

        try {
            const credits = await getUserCredits(user.uid);
            console.log('Current credits:', credits.balance);
            return credits.balance <= 0;
        } catch (error) {
            console.error('Error checking credits:', error);
            return true; // On error, assume limited
        }
    }

    static async reportSearchToGTM(keyword: string) {
        if (!keyword) return;
        const STORAGE_KEY = "user_search_count";
        const currentCount = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10) + 1;
        localStorage.setItem(STORAGE_KEY, currentCount.toString());

        // @ts-ignore
        if (typeof window.gtag === 'function') {
            // @ts-ignore
            window.gtag('event', 'search', {
                search_term: keyword,
                search_count: currentCount,
                method: "custom_input",
            });

            console.log("[GTM] Search recorded:", { keyword, currentCount });
        } else {
            console.warn("[GTM] gtag function not available.");
        }
    }
}
